import { ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Download, Plus, Search, Shield, Table2, Upload } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { Input } from "../../components/Input/Input";
import {
  Menu,
  type MenuItem,
  type MenuSeparator,
} from "../../components/Menu/Menu";
import { Select } from "../../components/Select/Select";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { useToast } from "../../components/Toast/toastContext";
import { saveBlob } from "../../lib/download";
import { useProject } from "../projects/useProject";
import { DEFAULT_CONFIG, normalizeConfig } from "../projects/config/defaults";
import { DEFAULT_BOTS_CONFIG, normalizeBots } from "./config/defaults";
import { botSettingsQueryKey } from "./useBotSettingsDraft";
import { getBotSettings } from "./api";
import { guildsListQueryKey } from "../guilds/queryKeys";
import {
  createGuildTemplate,
  deleteGuildTemplate,
  exportGuildTemplatesCSV,
  getBotsOverview,
  guildTemplateError,
  listGuildTemplates,
  updateGuildTemplate,
} from "./api";
import { CreateGuildsModal } from "./CreateGuildsModal";
import { EditGuildTemplateModal } from "./EditGuildTemplateModal";
import { ImportGuildTemplatesModal } from "./ImportGuildTemplatesModal";
import { sourceLink } from "./links";
import type { BotSource } from "./config/types";
import {
  blockedLabel,
  carries,
  cardKeysOf,
  cellValue,
  columnID,
  copyName,
  formatCount,
  isNumberType,
  PAGE_SIZE,
  plural,
  SOURCE_LABEL,
  templateCsvHeader,
  type CardKey,
  type EditState,
} from "./roster";
import type {
  CreateGuildsPlan,
  GuildTemplate,
  GuildTemplateInput,
  GuildTemplateSummary,
} from "./types";
import { useDebounced } from "./useDebounced";
import styles from "./GuildTemplatesTab.module.css";

/**
 * `?tab=guilds` — the guilds an operator describes before any of them exist.
 *
 * The population worker FILLS guilds and never creates one, and that is not an
 * omission: a guild invented by a simulation is a guild nobody named. So the
 * guilds are written down here first — a name, a description, and whatever
 * `guild.metadata_schema` declares — and turning them into real guilds is one
 * deliberate button, never a schedule.
 *
 * Two rules of this table are decisions rather than behaviour, and both are
 * visible on screen because they are surprising otherwise:
 *
 *   - a row is spent FOR GOOD. Its guild being disbanded does not free it, and
 *     the warn alert under the table says so — a name that came back round on
 *     its own is how a project ends up with the same guild twice in a month.
 *     «Create this guild again» in the row menu is the operator's own click,
 *     and it is ONE click: the menu item opens the run, and the row is freed
 *     inside that run, after the operator has pressed Create. Freeing on the
 *     menu press and opening the dialog afterwards threw the history away
 *     before anybody had agreed to anything, and Cancel could not put it back;
 *   - a spent row cannot be edited or deleted. The row is the only record that
 *     the platform stood that guild up — the admin guilds list reads it — so
 *     the menu of a spent row offers neither, rather than offering items that
 *     exist to come back 409.
 *
 * A row is WRITTEN here as well as imported. The dialog could always create —
 * «Duplicate» opens it with no row behind it — but the table an operator meets
 * first has nothing to duplicate, so the toolbar and the empty state open the
 * same dialog on an empty form and the CSV pair goes back to being what it is:
 * how a hundred rows arrive at once.
 *
 * Mockups: t-103/bots-guild-templates.html, t-108/bots-guild-template-create.html.
 */

/** What the dialog opens with when the toolbar asks for a run — the mockup's
 *  five, or fewer when the table cannot pay for five. */
const DEFAULT_RUN = 5;

export function GuildTemplatesTab({ projectID }: { projectID: string }) {
  const qc = useQueryClient();
  const { push } = useToast();

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<EditState<
    GuildTemplate,
    GuildTemplateInput
  > | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [run, setRun] = useState<RunState | null>(null);
  const [exporting, setExporting] = useState(false);
  // The box answers every keystroke; the server answers the word.
  const settledQ = useDebounced(q);

  // The same cache entry the Source and Settings tabs read: the project's own
  // schema decides this table's columns, and a second copy of it would be a
  // second answer to «what is on a guild».
  const project = useProject(projectID);
  const botSettings = useQuery({
    queryKey: botSettingsQueryKey(projectID),
    queryFn: () => getBotSettings(projectID),
    enabled: !!projectID,
  });

  const templates = useQuery({
    queryKey: ["project", projectID, "bots", "templates", settledQ, status],
    queryFn: () =>
      listGuildTemplates(projectID, { q: settledQ, status, limit: PAGE_SIZE }),
    enabled: !!projectID,
    // The previous page stays on screen while the next one is fetched: a table
    // that emptied into a skeleton between two letters would be a screen
    // blinking at the operator rather than answering them.
    placeholderData: (prev) => prev,
  });

  // The page above this one already asks for it, so this costs nothing: the
  // empty screen needs to say where the bots that would fill these guilds are
  // to come from, and that is the overview's `source` and `source_left`.
  const overview = useQuery({
    queryKey: ["project", projectID, "bots", "overview"],
    queryFn: () => getBotsOverview(projectID),
    enabled: !!projectID,
  });

  // DEFAULT_CONFIG is only ever the value of a render that will not happen:
  // the tab returns before it draws anything while the project is in flight.
  // Drawing against it would emit a «Download template» header with none of
  // this project's `meta:` columns in it, and check the create dialog's
  // starting size against a max_members of 30 that belongs to no project.
  const config = useMemo(
    () => (project.data ? normalizeConfig(project.data.config) : DEFAULT_CONFIG),
    [project.data],
  );
  // The project's guild metadata, read with the roster's own vocabulary: one
  // declared key is one column, and `meta:region` is both its column id and
  // its name in the CSV.
  // The create dialog quotes the population rules back at the operator — how
  // many of the guilds it is about to stand up will actually be filled, and how
  // fast. Those live in the simulation's own document now, so the tab reads it
  // rather than digging a key out of the project config.
  const settings = useMemo(
    () => (botSettings.data ? normalizeBots(botSettings.data.config) : DEFAULT_BOTS_CONFIG()),
    [botSettings.data],
  );
  const metaKeys = useMemo(
    () => cardKeysOf([], config.guild.metadata_schema),
    [config.guild.metadata_schema],
  );
  const csvHeader = useMemo(() => templateCsvHeader(metaKeys), [metaKeys]);

  const rows = templates.data?.rows ?? [];
  const summary = templates.data?.summary;
  const filtered = settledQ !== "" || status !== "";
  // `not_created` and `blocked` are DISJOINT — the summary's four counters
  // partition the table — so the usable count is `not_created` itself. It used
  // to have `blocked` subtracted out of it, on the belief that blocked rows
  // were counted inside it; they are not, and the subtraction quietly asked for
  // one guild fewer than the table could pay for.
  const usable = summary?.not_created ?? 0;
  /**
   * Which of the two the toolbar accents — and NEITHER while the summary is
   * out, because `usable` reads 0 for «none left» and for «not asked yet»
   * alike. The tile above draws «—» in exactly that state, and the alert
   * under a failed list says the screen knows nothing about the table; an
   * accent on «New template» beside it would be the same screen answering
   * «there is nothing to create guilds from» in the next breath. A failed
   * list has no answer, and neither does a list still in flight.
   */
  const accent: "run" | "write" | null = summary
    ? usable > 0
      ? "run"
      : "write"
    : null;
  // The blocked rows this page can NAME. The count is the table's; the names
  // are only ever of rows actually loaded, so a filter that hides them falls
  // back to the count rather than inventing a name.
  const blockedNames = rows
    .filter((r) => r.status === "blocked")
    .map((r) => r.name);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["project", projectID, "bots"] });

  /**
   * A run STOOD guilds up, so the project's guild list is stale too — and it
   * does not hang off `["project", id, "bots"]`, it has a root of its own.
   * Without this the operator who goes straight to Guilds to look at the
   * result — which is what the mockup's «44 guilds · 5 created just now»
   * promises — reads a list with neither the new guilds nor the «founded by
   * simulation» badge on them for the next thirty seconds.
   */
  const invalidateAfterRun = () => {
    void invalidate();
    void qc.invalidateQueries({ queryKey: guildsListQueryKey(projectID) });
  };

  const saveRow = useMutation({
    mutationFn: ({
      row,
      input,
    }: {
      row: GuildTemplate | null;
      input: GuildTemplateInput;
    }) =>
      row
        ? updateGuildTemplate(projectID, row.id, input)
        : createGuildTemplate(projectID, input),
    onSuccess: async (_res, { row }) => {
      setEditing(null);
      setRowError(null);
      await invalidate();
      push({
        variant: "success",
        message: row ? "Template saved." : "Template added.",
      });
    },
    onError: (err) => setRowError(guildTemplateError(err)),
  });

  const removeRow = useMutation({
    mutationFn: (row: GuildTemplate) => deleteGuildTemplate(projectID, row.id),
    onSuccess: async () => {
      await invalidate();
      push({ variant: "success", message: "Template deleted." });
    },
    onError: (err) => push({ variant: "danger", message: guildTemplateError(err) }),
  });

  async function onExport() {
    setExporting(true);
    try {
      await exportGuildTemplatesCSV(projectID);
    } catch (err) {
      push({ variant: "danger", message: guildTemplateError(err) });
    } finally {
      setExporting(false);
    }
  }

  function onEdit(row: GuildTemplate) {
    setRowError(null);
    setEditing({ row, input: inputOf(row), title: "Edit template" });
  }

  /**
   * The empty form. The dialog has always been able to create — «Duplicate»
   * opens it with `row: null` — but a table with no rows has nothing to
   * duplicate, and the only way in was a CSV file. So one guild is written
   * here and a hundred still arrive through the import.
   */
  function onCreate() {
    setRowError(null);
    setEditing({
      row: null,
      input: { name: "", description: "", logo: "", metadata: {} },
      title: "New template",
    });
  }

  function onDuplicate(row: GuildTemplate) {
    setRowError(null);
    setEditing({
      row: null,
      input: { ...inputOf(row), name: copyName(row.name) },
      title: "Duplicate template",
    });
  }

  /**
   * A run that finished. A PARTIAL one never arrives here: the dialog keeps
   * itself open for that and names the guilds that are standing, because a
   * toast that fades in five seconds is not where a list an operator has to go
   * and tidy up belongs.
   */
  function onCreated(plan: CreateGuildsPlan) {
    setRun(null);
    invalidateAfterRun();
    push({
      variant: "success",
      title: "Guilds created",
      message: `${plural(plan.guilds.length, "guild", "guilds")} · ${plural(plan.total_bots, "bot", "bots")} seated.`,
    });
  }

  // The project's own schema decides this table's columns, the CSV header the
  // empty state hands out, and the ceiling the create dialog checks a starting
  // size against. None of the three has an honest value until it lands — and
  // a request that FAILED never lands, so «Loading…» here was a spinner that
  // never stopped rather than an answer.
  if (project.isError) {
    return (
      <div className={styles.alertStack}>
        <Alert
          variant="danger"
          title="Couldn't read this project"
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void project.refetch()}
            >
              Try again
            </Button>
          }
        >
          The table's columns, the header its import is read against and the
          size a new guild may reach all come out of this project's own schema.
          None of them can be drawn until it answers.
        </Alert>
      </div>
    );
  }
  if (project.isLoading || !project.data) return <div>Loading…</div>;

  return (
    <>
      {/*
        A failed list says NOTHING about the table. Without this the three
        tiles read «—», the table fell to «No guild templates yet» and the
        screen invited an operator with a hundred rows to import them again.
      */}
      {templates.isError && (
        <div className={styles.alertStack}>
          <Alert
            variant="danger"
            title="Couldn't read the templates"
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void templates.refetch()}
              >
                Try again
              </Button>
            }
          >
            The rows are where they were — nothing on this page has touched
            them. Until this loads there is no telling how many there are or how
            many are still to be created.
          </Alert>
        </div>
      )}

      <Stats summary={summary} />

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name…"
            aria-label="Search templates by name"
            leadingIcon={<Search size={14} aria-hidden="true" />}
          />
        </div>
        <Select
          aria-label="Status"
          value={status}
          onChange={setStatus}
          options={[
            { value: "", label: "Status: all" },
            { value: "created", label: "Status: created" },
            { value: "guild_disbanded", label: "Status: guild disbanded" },
            { value: "not_created", label: "Status: not created" },
            // The value is the API's `blocked`; the label is what the row's own
            // chip says. An operator who has just read «name taken» off a row
            // should not have to work out that «blocked» is the same answer.
            { value: "blocked", label: "Status: name taken" },
          ]}
        />
        <span className={styles.spacer} />
        {/*
          «New template» stands left of the CSV pair because it is about the
          TABLE and the pair is about a FILE. The accent moves between it and
          «Create guilds…» — never the buttons themselves — and it moves on the
          one number the page already holds: with rows waiting to be created the
          point of the table is to turn them into guilds, and with none waiting
          (an empty table, or one spent to the last row) the only next step there
          is is to write another template — and neither of them while that
          number is still out, see `accent` above. Two accents in one row would
          ask the operator the question instead of answering it.
        */}
        <Button
          variant={accent === "write" ? "primary" : "secondary"}
          icon={<Plus size={14} />}
          onClick={onCreate}
        >
          New template
        </Button>
        <Button
          variant="secondary"
          icon={<Download size={14} />}
          loading={exporting}
          onClick={onExport}
        >
          Export CSV
        </Button>
        <Button
          variant="secondary"
          icon={<Upload size={14} />}
          onClick={() => setImportOpen(true)}
        >
          Import CSV
        </Button>
        <Button
          variant={accent === "run" ? "primary" : "secondary"}
          icon={<Plus size={14} />}
          onClick={() =>
            setRun({
              count: Math.max(1, Math.min(DEFAULT_RUN, usable)),
            })
          }
        >
          Create guilds…
        </Button>
      </div>

      <TemplatesTable
        rows={rows}
        total={templates.data?.total ?? 0}
        loading={templates.isLoading}
        metaKeys={metaKeys}
        projectID={projectID}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={(row) => removeRow.mutate(row)}
        onCreateOne={(row) =>
          setRun({ templateIDs: [row.id], count: 1, name: row.name })
        }
        onCreateAgain={(row) =>
          setRun({
            templateIDs: [row.id],
            count: 1,
            name: row.name,
            freeID: row.id,
          })
        }
        empty={
          // «No templates yet» is an ANSWER — a successful response with an
          // empty table in it. A failed one has no answer to give, and the
          // alert above is the whole of what this screen knows.
          templates.isError ? (
            <EmptyState
              icon={<Shield size={20} />}
              title="The table could not be read"
              description="This is the request, not the table. Nothing has been deleted and nothing has been imported."
            />
          ) : filtered ? (
            <EmptyState
              title="No template matches"
              description="Clear the search or the status filter to see the rest of the table."
            />
          ) : (
            <EmptyState
              icon={<Shield size={20} />}
              title="No guild templates yet"
              description="Bots fill guilds that already exist. To have them stand up guilds of their own, describe those guilds here first — and make sure the Source has bots to seat in them."
              action={
                <div className={styles.emptyActions}>
                  {/* Writing one guild down is what this screen is for; the
                      file is how a hundred arrive, and «Download template» is
                      what keeps that first import off guesswork about column
                      names. */}
                  <Button icon={<Plus size={14} />} onClick={onCreate}>
                    New template
                  </Button>
                  <Button
                    variant="secondary"
                    icon={<Upload size={14} />}
                    onClick={() => setImportOpen(true)}
                  >
                    Import CSV
                  </Button>
                  <Button
                    variant="secondary"
                    icon={<Download size={14} />}
                    onClick={() =>
                      saveBlob(
                        new Blob([`${csvHeader}\r\n`], {
                          type: "text/csv;charset=utf-8",
                        }),
                        "bot-guild-templates-template.csv",
                      )
                    }
                  >
                    Download template
                  </Button>
                </div>
              }
              // Not a second child of `action`: that slot is an inline-flex
              // row, and this line went in BESIDE the buttons with its rule
              // drawn as a stub the width of itself.
              footer={
                <SourceLine
                  projectID={projectID}
                  source={overview.data?.overview.source}
                  left={overview.data?.overview.source_left ?? 0}
                />
              }
            />
          )
        }
      />

      <Disbanded
        rows={rows}
        total={summary?.guild_disbanded ?? 0}
        onShow={() => {
          setQ("");
          setStatus("guild_disbanded");
        }}
      />

      {importOpen && (
        <ImportGuildTemplatesModal
          projectID={projectID}
          csvHeader={csvHeader}
          metaKeys={metaKeys}
          onClose={() => setImportOpen(false)}
          onDone={(res) => {
            setImportOpen(false);
            void invalidate();
            push({
              variant: "success",
              title: "Import finished",
              message:
                res.removed > 0
                  ? `${plural(res.added, "template", "templates")} written · ${plural(res.removed, "unused row", "unused rows")} replaced · ${formatCount(res.refused)} refused.`
                  : `${plural(res.added, "template", "templates")} written · ${formatCount(res.refused)} refused.`,
            });
          }}
        />
      )}

      {editing && (
        <EditGuildTemplateModal
          projectID={projectID}
          row={editing.row}
          initial={editing.input}
          title={editing.title}
          metaKeys={metaKeys}
          allowedLogos={config.guild_assets.allowed_logos}
          takenNames={rows
            .filter((r) => r.id !== editing.row?.id)
            .map((r) => r.name)}
          saving={saveRow.isPending}
          error={rowError}
          onClose={() => {
            setEditing(null);
            setRowError(null);
          }}
          onSave={(input) => saveRow.mutate({ row: editing.row, input })}
        />
      )}

      {run && (
        <CreateGuildsModal
          projectID={projectID}
          templateIDs={run.templateIDs}
          initialCount={run.count}
          onlyTemplate={run.name}
          freeTemplateID={run.freeID}
          onFreed={() => void invalidate()}
          // A partial run leaves standing guilds behind it — the list of the
          // project has to move for those too.
          onPartial={invalidateAfterRun}
          blocked={summary?.blocked ?? 0}
          blockedNames={blockedNames}
          maxMembers={config.guild.max_members}
          joinPercent={settings.population.join_probability}
          targetMembersMin={settings.population.target_members_min}
          targetMembers={settings.population.target_members}
          onClose={() => setRun(null)}
          onDone={onCreated}
        />
      )}
    </>
  );
}

/** What the create dialog was opened for: the whole table, or one named row. */
interface RunState {
  templateIDs?: string[];
  count: number;
  /** The row's name, when the run is about exactly one — the dialog says so
   *  instead of pretending the operator asked for «the next 1 from the top». */
  name?: string;
  /** Set when the run is «Create this guild again»: the row is SPENT, and
   *  freeing it is the first half of the click that founds the guild. It is
   *  not done here, because a Cancel here has to leave the row alone. */
  freeID?: string;
}

function inputOf(row: GuildTemplate): GuildTemplateInput {
  return {
    name: row.name,
    description: row.description,
    // A server that predates the column sends no logo at all; the dialog holds
    // it as a string, and «no logo» is the empty one.
    logo: row.logo ?? "",
    metadata: row.metadata ?? {},
  };
}

/* --- the three tiles ------------------------------------------------------ */

function Stats({ summary }: { summary: GuildTemplateSummary | undefined }) {
  const s = summary;
  return (
    <div className={styles.statGrid}>
      <Stat
        label="Templates"
        icon={<Table2 size={14} />}
        value={s ? formatCount(s.total) : "—"}
        sub="in the table"
      />
      <Stat
        label="Standing"
        icon={<Shield size={14} />}
        value={s ? formatCount(s.created) : "—"}
        sub={
          s && s.guild_disbanded > 0
            ? `guilds alive · ${formatCount(s.guild_disbanded)} disbanded`
            : "guilds alive"
        }
        subTone={s && s.guild_disbanded > 0 ? "warn" : undefined}
      />
      <Stat
        label="Not created"
        icon={<Clock size={14} />}
        value={s ? formatCount(s.not_created) : "—"}
        // The number is the USABLE one: blocked rows are their own counter and
        // are not inside this one. So the sub-line says «more», not a share —
        // a tile reading 6 over a list of five, with one of the five badged
        // «name taken», was two statements that could not both be true.
        sub={
          s && s.blocked > 0
            ? s.blocked === 1
              ? "1 more blocked by its name"
              : `${formatCount(s.blocked)} more blocked by their names`
            : "ready for the next run"
        }
        subTone={s && s.blocked > 0 ? "warn" : undefined}
        muted={!!s && s.not_created === 0}
      />
    </div>
  );
}

function Stat({
  label,
  icon,
  value,
  sub,
  subTone,
  muted,
}: {
  label: string;
  icon: ReactNode;
  value: string;
  sub: string;
  subTone?: "warn";
  muted?: boolean;
}) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statHead}>
        <span className={styles.statLabel}>{label}</span>
        <span className={styles.statIcon} aria-hidden="true">
          {icon}
        </span>
      </div>
      <div
        className={`${styles.statValue} ${muted ? styles.statValueMuted : ""}`}
      >
        {value}
      </div>
      <div
        className={`${styles.statSub} ${subTone === "warn" ? styles.statSubWarn : ""}`}
      >
        {sub}
      </div>
    </div>
  );
}

/* --- the table ------------------------------------------------------------ */

interface TableProps {
  rows: GuildTemplate[];
  /** Rows in the whole table, not on this page — what the footer counts. */
  total: number;
  loading: boolean;
  metaKeys: CardKey[];
  projectID: string;
  onEdit: (row: GuildTemplate) => void;
  onDuplicate: (row: GuildTemplate) => void;
  onDelete: (row: GuildTemplate) => void;
  onCreateOne: (row: GuildTemplate) => void;
  onCreateAgain: (row: GuildTemplate) => void;
  empty: ReactNode;
}

function TemplatesTable(p: TableProps) {
  if (p.loading && p.rows.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.tableLoading}>
          <Skeleton height={18} />
          <Skeleton height={18} />
          <Skeleton height={18} />
        </div>
      </div>
    );
  }

  if (p.rows.length === 0) return <div className={styles.card}>{p.empty}</div>;

  return (
    <div className={styles.card}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Description</th>
              {/* Between the description and the schema's own columns, which
                  is where the dialog and the CSV put it too: the logo is a
                  field of the guild, not a key of its metadata. It is here at
                  all because a batch of templates arrives as a FILE and is
                  checked by eye against this table. */}
              <th scope="col">Logo</th>
              {p.metaKeys.map((k) => (
                <th
                  key={columnID(k)}
                  scope="col"
                  className={isNumberType(k) ? styles.num : undefined}
                >
                  {k.key}
                </th>
              ))}
              <th scope="col">Status</th>
              {/* The row menu's column. Its own button carries the name of the
                  row it belongs to, so a header here would only repeat
                  «Actions» once per row. */}
              <th className={styles.act} />
            </tr>
          </thead>
          <tbody>
            {p.rows.map((row) => (
              <tr
                key={row.id}
                className={row.status === "blocked" ? styles.bad : undefined}
              >
                <td>
                  <div className={styles.whoName}>{row.name}</div>
                </td>
                <td className={styles.descCell}>{row.description || "—"}</td>
                {/* The asset id as it stands, mono like the metadata cells
                    beside it — it is an id the game resolves, and nothing here
                    resolves it to a picture. */}
                <td className={styles.mono}>{row.logo || "—"}</td>
                {p.metaKeys.map((k) => {
                  const bag = row.metadata ?? {};
                  return (
                    <td
                      key={columnID(k)}
                      className={isNumberType(k) ? styles.num : styles.mono}
                    >
                      {carries(bag, k.key) ? cellValue(bag[k.key]) : "—"}
                    </td>
                  );
                })}
                <td>
                  <StatusCell
                    row={row}
                    projectID={p.projectID}
                    onRename={() => p.onEdit(row)}
                  />
                </td>
                <td className={styles.act}>
                  <Menu
                    aria-label={`Actions for ${row.name}`}
                    items={rowMenu(row, p)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.cardFoot}>
        <span>
          {plural(p.total, "template", "templates")} · showing {p.rows.length} ·
          created top to bottom
        </span>
        <span className={styles.mono}>
          bot_guild_templates · metadata validated against
          guild.metadata_schema
        </span>
      </div>
    </div>
  );
}

/**
 * The row menu.
 *
 * A SPENT row — created, or created and since disbanded — is offered two items
 * and not five. Editing it and deleting it both come back `template_spent`:
 * the row is the record of where that guild came from, and the admin guilds
 * list reads it. What it is offered instead is «Create this guild again»,
 * which frees the row and opens the run that spends it a second time — and
 * that one is refused while the guild is STANDING, because founding it twice
 * would put two guilds of one name into the project and leave the first one
 * with no row pointing at it. The way to a second guild like this one is
 * Duplicate, which writes a row of its own.
 *
 * A BLOCKED row keeps every item, with the one that cannot work refused where
 * the pressing would have happened: a guild of this project already carries
 * that name, and no run will pick the row up until it is renamed.
 */
function rowMenu(
  row: GuildTemplate,
  p: Pick<
    TableProps,
    "onEdit" | "onDuplicate" | "onDelete" | "onCreateOne" | "onCreateAgain"
  >,
): (MenuItem | MenuSeparator)[] {
  const spent = row.status === "created" || row.status === "guild_disbanded";
  if (spent) {
    return [
      {
        label: "Create this guild again",
        onSelect: () => p.onCreateAgain(row),
        disabled: row.status === "created",
        hint: row.status === "created" ? "guild standing" : undefined,
      },
      { label: "Duplicate", onSelect: () => p.onDuplicate(row) },
    ];
  }
  return [
    { label: "Edit template…", onSelect: () => p.onEdit(row) },
    {
      label: "Create this guild now",
      onSelect: () => p.onCreateOne(row),
      disabled: row.status === "blocked",
      hint: row.status === "blocked" ? "name taken" : undefined,
    },
    { label: "Duplicate", onSelect: () => p.onDuplicate(row) },
    { separator: true },
    {
      label: "Delete template",
      variant: "danger",
      onSelect: () => p.onDelete(row),
    },
  ];
}

function StatusCell({
  row,
  projectID,
  onRename,
}: {
  row: GuildTemplate;
  projectID: string;
  onRename: () => void;
}) {
  if (row.status === "created") {
    return (
      <span className={styles.statusCell}>
        <Badge variant="success" dot>
          created
        </Badge>
        {row.used_guild_id && (
          <Link
            className={styles.rowLink}
            to={`/projects/${projectID}/guilds/${row.used_guild_id}`}
          >
            {row.name} ›
          </Link>
        )}
      </span>
    );
  }
  if (row.status === "guild_disbanded") {
    return (
      <Badge variant="warn" dot>
        guild disbanded
      </Badge>
    );
  }
  if (row.status === "blocked") {
    return (
      <span className={styles.statusCell}>
        <Badge variant="danger" dot>
          {blockedLabel(row.blocked_reason)}
        </Badge>
        {/* The fix is in the dialog this opens: a guild of this project
            carries the name, so the row has to give it up. */}
        <button type="button" className={styles.rowLink} onClick={onRename}>
          Rename ›
        </button>
      </span>
    );
  }
  return (
    <Badge variant="neutral" dot>
      not created
    </Badge>
  );
}

/**
 * The rows whose guilds are gone, and the sentence that says the row is still
 * spent.
 *
 * It is counted off the SUMMARY and named off the rows on screen, and the split
 * is the point. Counting it off the page meant any search or status filter took
 * the alert away — and with it the only pointer to «Create this guild again» —
 * while the tile above went on counting the same rows. So the count is the
 * table's, the name is only used when a row carrying it is actually visible,
 * and when none is the alert offers the filter that brings them back.
 */
function Disbanded({
  rows,
  total,
  onShow,
}: {
  rows: GuildTemplate[];
  /** summary.guild_disbanded — the whole table, not this page. */
  total: number;
  /** Narrow the table to those rows, so the row menu is reachable. */
  onShow: () => void;
}) {
  if (total === 0) return null;
  const gone = rows.filter((r) => r.status === "guild_disbanded");
  return (
    <div className={styles.alertStack}>
      <Alert
        variant="warn"
        title={
          total === 1
            ? gone.length === 1
              ? `«${gone[0].name}» stood and is gone`
              : "1 template stood and its guild is gone"
            : `${plural(total, "template", "templates")} stood and their guilds are gone`
        }
      >
        {total === 1 ? "Its guild was" : "Their guilds were"} disbanded — the
        last living member left, or an operator deleted{" "}
        {total === 1 ? "it" : "them"}. The row is <b>not</b> freed
        automatically: a name coming back around by itself is how a project ends
        up with the same guild twice in a month. «Create this guild again» in the
        row menu is the deliberate way to reuse it.
        {gone.length === 0 && (
          <>
            {" "}
            <button type="button" className={styles.alertBtn} onClick={onShow}>
              Show {total === 1 ? "that row" : "those rows"}
            </button>
          </>
        )}
      </Alert>
    </div>
  );
}

/**
 * Where the bots that would fill these guilds come from, on the screen that has
 * no guilds yet. A guild with no members is not a showcase, so the empty table
 * has to lead to the roster as well as to this table's own import.
 */
function SourceLine({
  projectID,
  source,
  left,
}: {
  projectID: string;
  source: BotSource | undefined;
  left: number;
}) {
  if (!source) return null;
  return (
    <div className={styles.emptyFoot}>
      Source: <b>{SOURCE_LABEL[source]}</b>
      {source === "table"
        ? ` · ${plural(left, "row", "rows")} left`
        : " · names are drawn from the pool"}{" "}
      <Link className={styles.rowLink} to={sourceLink(projectID)}>
        Open Source ›
      </Link>
    </div>
  );
}
